import PdfIcon from "@mui/icons-material/PictureAsPdf";
import type { SxProps } from "@mui/material";
import type { FC, ReactNode } from "react";
import type { DownloadOption } from "./MultiOptionDownloadButton";
import MultiOptionDownloadButton from "./MultiOptionDownloadButton";
import PrimaryMenuDownloadButton from "./PrimaryMenuDownloadButton";
import SingleOptionDownloadButton from "./SingleOptionDownloadButton";
import { useDownloadFile } from "./useDownloadFile";

interface Props {
  readonly buttonText?: string;
  readonly startIcon?: ReactNode;
  readonly variant?: "outlined" | "contained" | "text";
  readonly color?: "primary" | "secondary" | "error" | "inherit";
  readonly options: DownloadOption[];
  readonly filename: string | ((key?: string) => string);
  readonly sx?: SxProps;
  /** First option downloads on main click; remaining options appear in the dropdown */
  readonly primaryOption?: boolean;
  onError: (error: unknown) => void;
}

const DownloadFileButton: FC<Props> = ({
  buttonText = "Pobierz PDF",
  variant,
  color,
  startIcon,
  options,
  onError,
  sx,
  filename,
  primaryOption = false,
}) => {
  const { download, isLoading } = useDownloadFile({
    apiDefinition: (key) => {
      const def = options.find((o) => o.name === key)?.apiDefinition;
      if (!def) {
        throw new Error(`No download option for key ${key}`);
      }
      return def();
    },
    onError: (error) => {
      onError(error);
    },
    filename,
  });
  const icon = startIcon ?? <PdfIcon />;

  if (options.length === 1) {
    return (
      <SingleOptionDownloadButton
        loading={isLoading}
        icon={icon}
        variant={variant}
        color={color}
        sx={sx}
        onDownload={() => download(options[0].name)}
      >
        {buttonText}
      </SingleOptionDownloadButton>
    );
  }

  if (primaryOption) {
    const [primary, ...secondary] = options;
    return (
      <PrimaryMenuDownloadButton
        loading={isLoading}
        primaryOption={primary}
        secondaryOptions={secondary}
        icon={icon}
        variant={variant}
        color={color}
        sx={sx}
        buttonText={buttonText ?? primary.name}
        onDownload={download}
      />
    );
  }

  return (
    <MultiOptionDownloadButton
      loading={isLoading}
      options={options}
      icon={icon}
      variant={variant}
      color={color}
      sx={sx}
      onDownload={download}
    >
      {buttonText}
    </MultiOptionDownloadButton>
  );
};

export default DownloadFileButton;
